// Public API of the world layer: the real world as a Civ game state, at any date since 2000.
// The services layer is the only caller. Implementations live beside this file.

import type {
  ActorId, City, Claim, Country, DateStr, Deposit, LeaderId, Perspective, SourceId, Statement, War, Wonder, WorldEvent,
} from '../core/schema.ts';

/** Civ 6 diplomacy states, derived by one rule set for every leader (ARCHITECTURE.md §5). */
export type Relation = 'self' | 'war' | 'ally' | 'friend' | 'neutral' | 'denounced';

export interface RelationInfo {
  relation: Relation;
  /** Why: the rule that fired and its sources. */
  reason: string;
  sources: SourceId[];
}

export const CONTROL = { NONE: 0, OCCUPIED: 1, CONTESTED: 2, ANNEXATION_CLAIMED: 3, BREAKAWAY: 4 } as const;

/** Bump when relation, victory, change or indicator rules change meaning (part of the sandbox rules version). */
export const WORLD_RULES_VERSION = '1';

/** A sandbox viewpoint: any state, under the default (UN-majority) worldview, with no leader. */
export interface StateView {
  iso3: string;
}

/** Whose eyes a query looks through: a playable leader (real mode) or a state (sandbox). */
export type Viewer = LeaderId | StateView;

export interface WorldOptions {
  /** 'off': every snapshot's staleness is one shared all-zero array (no parchment). Default 'on'. */
  fog?: 'on' | 'off';
}

/** Everything the map needs for one date and one leader. Arrays are per hex (grid.ts index). */
export interface WorldSnapshot {
  date: DateStr;
  /** The playable leader; null for a StateView (sandbox: states, not people). */
  leader: LeaderId | null;
  /** The iso3 whose relations `relations` holds (the leader's state, or the StateView's). createWorld always sets it. */
  viewer?: string;
  /** Actor index (see actorAt) of the de jure owner under the leader's point of view; 0 = none. */
  owner: Uint16Array;
  /** Actor index of the de facto controller when it differs from owner; 0 = same as owner. */
  controller: Uint16Array;
  /** CONTROL value per hex. */
  control: Uint8Array;
  /** 0..255 share of the hex's land under `controller`. */
  controlFrac: Uint8Array;
  /** 0 = sourced recently … 255 = no source for a long time (drives parchment fog). */
  staleness: Uint8Array;
  /** Where the leader's point of view assigns a hex to a different state than the default
   * (UN-majority) worldview: the actor index of that default owner, so the map can hatch the hex
   * and say "shown as <owner> in this view; recognised as <claim>". 0 = no disagreement. */
  claim?: Uint16Array;
  /** Sea-ice concentration 0..255 on water hexes for the date's month (NSIDC); absent = no data. */
  seaIce?: Uint8Array;
  /** Geolocated resource deposits (static; each carries its sources). */
  deposits?: Deposit[];
  /** Front lines in force at the date (e.g. the DeepState snapshot ≤ date), as lon/lat polylines. */
  fronts?: Array<{ id: string; controller: ActorId; lines: number[][]; asOf: DateStr; sources: SourceId[] }>;
  /** Relation of every actor index to the leader's state. */
  relations: Map<number, RelationInfo>;
  activeWars: War[];
  /** Cities that exist at this date, with population for the nearest year. */
  cities: City[];
  /** World Heritage sites inscribed by this date. */
  wonders: Wonder[];
}

export interface VictoryEntry {
  iso3: string;
  /** 0..1 share of the track. */
  progress: number;
  /** Human-readable breakdown for the tooltip, e.g. 'PPP 19.5% · nominal 16.5% · exports 11.8%'. */
  detail: string;
  /** Year of the newest data point used; the UI greys values older than 3 years. */
  dataYear: number;
  sources: SourceId[];
}

export type VictoryTrack = 'space' | 'tech' | 'economic' | 'diplomatic' | 'culture' | 'score';

export interface LensResult {
  id: string;
  /** Per-hex scalar 0..1 (or category index) for the shader; NaN = no data. */
  values: Float32Array;
  kind: 'scalar' | 'category';
  legend: Array<{ label: string; color: string }>;
  sources: SourceId[];
  /** Data year shown in the legend. */
  year?: number;
}

export interface Grievance {
  /** Who acted. */
  by: string;
  target: string;
  kind: 'sanction' | 'tariff' | 'war' | 'strike' | 'occupation';
  title: string;
  scope?: string;
  from: DateStr;
  to: DateStr | null;
  sources: SourceId[];
}

/** Hover-card facts for one hex. */
export interface HexDetail {
  hex: number;
  lat: number;
  lon: number;
  terrain: string;
  feature: string | null;
  hill: string | null;
  elevM: number;
  river: 'none' | 'river' | 'major';
  owner: string | null;
  controller: string | null;
  control: string | null;
  city: City | null;
  wonder: Wonder | null;
  resource: string | null;
  sources: SourceId[];
}

export interface CountryReport {
  country: Country;
  leaders: Array<{ role: string; name: string; since: DateStr; sources: SourceId[] }>;
  memberships: Array<{ bloc: string; status: string; since: DateStr; sources: SourceId[] }>;
  wars: War[];
  relation: RelationInfo;
  /** Indicator id → latest value at the date with its year. Always includes, when data exists:
   * 'gdp' (current US$), 'population', 'hard_power' (0..1 share of world military spending). */
  stats: Record<string, { value: number; year: number; unit: string; sources: SourceId[] }>;
  statements: Statement[];
  /** Sanctions, tariffs, wars, strikes and occupations between the leader's state and this one,
   * both directions, active at or before the date. */
  grievances: Grievance[];
}

export interface World {
  /** Latest date with data (the "today" of the app). */
  readonly asOf: DateStr;
  readonly countries: Country[];
  readonly perspectives: Perspective[];

  /** Actor index ↔ id. Countries keep their Country.id; non-state actors follow. */
  actorAt(index: number): { id: ActorId; name: string; colors: { primary: string; secondary: string } } | null;
  actorIndex(id: ActorId): number;

  snapshot(date: DateStr, viewer: Viewer): WorldSnapshot;
  victory(date: DateStr, track: VictoryTrack): VictoryEntry[];
  lens(id: string, date: DateStr, viewer: Viewer): LensResult;
  lensIds(): Array<{ id: string; name: string }>;
  hexInfo(hex: number, date: DateStr, viewer: Viewer): HexDetail;
  eventCounts(): { months: Array<[month: string, count: number]>; sources: SourceId[] };
  events(from: DateStr, to: DateStr): Promise<WorldEvent[]>;
  country(iso3: string, date: DateStr, viewer: Viewer): CountryReport;
  source(id: SourceId): { url: string; title: string; publisher: string; accessed: DateStr } | null;
  /** Status of any war at a date (claims are dated; null if the war did not exist). */
  warStatus(id: string, date: DateStr): Claim<string> | null;
}
